/**
 * Knowledge → Areas (9 Oct): the managed area tree articles are filed under.
 * Editors add, rename, re-key (bulk add's suggestion words), retire and
 * restore areas. An area's id never changes: v2's expertise map builds on it.
 */

import { Icon, Plus } from "@norican/icons";
import { Badge, Button, Card, Dialog, Field, Input, Typography } from "@norican/react";
import { useState } from "react";

import { AppBreadcrumbs, ErrorState, LoadingRows, PageContainer } from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import type { KnowledgeArea } from "@/lib/api";

import { useCreateKnowledgeArea, useKnowledgeAreas, useUpdateKnowledgeArea } from "./api";
import { areaOptions, parseTags } from "./lib/knowledge";
import { KnowledgeGate, OptionSelect } from "./shared";

export function KnowledgeAreasPage() {
  const { t } = useT();
  useDocumentTitle(t("knowledgeAdmin.areasPage.title"));
  return (
    <KnowledgeGate title={t("knowledgeAdmin.areasPage.title")} editorsOnly>
      <AreasBody />
    </KnowledgeGate>
  );
}

/** Indentation by depth in the tree (token-mapped spacing, no inline style). */
const INDENT = ["", "pl-6", "pl-12", "pl-16"] as const;

type Editing = { kind: "new"; parentId: string } | { kind: "edit"; area: KnowledgeArea };

function AreasBody() {
  const { t } = useT();
  const areas = useKnowledgeAreas();
  const update = useUpdateKnowledgeArea();
  const [editing, setEditing] = useState<Editing | null>(null);
  const items = areas.data?.items ?? [];
  const depth = new Map(areaOptions(items, true).map((o) => [o.value, o.depth]));

  return (
    <PageContainer
      title={t("knowledgeAdmin.areasPage.title")}
      subtitle={t("knowledgeAdmin.areasPage.subtitle")}
      breadcrumbs={
        <AppBreadcrumbs
          items={[
            { label: t("knowledgeAdmin.overview.title"), to: "/knowledge" },
            { label: t("knowledgeAdmin.areasPage.title") },
          ]}
        />
      }
      actions={
        <Button
          startIcon={<Icon icon={Plus} size="sm" />}
          onClick={() => setEditing({ kind: "new", parentId: "" })}
        >
          {t("knowledgeAdmin.areasPage.add")}
        </Button>
      }
    >
      {areas.isLoading ? (
        <LoadingRows rows={8} />
      ) : areas.error != null ? (
        <ErrorState error={areas.error} onRetry={() => void areas.refetch()} />
      ) : (
        <ul className="flex list-none flex-col gap-1 p-0">
          {items.map((area) => (
            <li key={area.id} className={INDENT[Math.min(depth.get(area.id) ?? 0, 3)]}>
              <Card variant={area.active ? "outline" : "subtle"} padding="sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={area.active ? "font-medium" : "text-fg-muted line-through"}>
                    {area.name}
                  </span>
                  <span className="text-fg-muted text-xs">{area.id}</span>
                  <Badge tone="neutral" variant="outline" size="xs">
                    {t("knowledgeAdmin.areasPage.articles", { count: area.article_count })}
                  </Badge>
                  {(area.keywords ?? []).length > 0 ? (
                    <span className="text-fg-muted truncate text-xs">
                      {(area.keywords ?? []).slice(0, 6).join(", ")}
                    </span>
                  ) : null}
                  <span className="ml-auto flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditing({ kind: "edit", area })}
                    >
                      {t("knowledgeAdmin.areasPage.edit")}
                    </Button>
                    {area.parent_id === null || area.parent_id === undefined ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing({ kind: "new", parentId: area.id })}
                      >
                        {t("knowledgeAdmin.areasPage.addChild")}
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={update.isPending && update.variables?.id === area.id}
                      onClick={() => update.mutate({ id: area.id, body: { active: !area.active } })}
                    >
                      {area.active
                        ? t("knowledgeAdmin.areasPage.retire")
                        : t("knowledgeAdmin.areasPage.restore")}
                    </Button>
                  </span>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
      {editing ? (
        <AreaDialog editing={editing} areas={items} onClose={() => setEditing(null)} />
      ) : null}
    </PageContainer>
  );
}

function AreaDialog({
  editing,
  areas,
  onClose,
}: {
  editing: Editing;
  areas: KnowledgeArea[];
  onClose: () => void;
}) {
  const { t } = useT();
  const create = useCreateKnowledgeArea();
  const update = useUpdateKnowledgeArea();
  const current = editing.kind === "edit" ? editing.area : null;
  const [name, setName] = useState(current?.name ?? "");
  const [description, setDescription] = useState(current?.description ?? "");
  const [keywords, setKeywords] = useState((current?.keywords ?? []).join(", "));
  const [parentId, setParentId] = useState(
    editing.kind === "new" ? editing.parentId : (current?.parent_id ?? ""),
  );
  const error = create.error ?? update.error;
  const parents = [
    { value: "", label: t("knowledgeAdmin.areasPage.topLevel") },
    ...areas
      .filter((a) => (a.parent_id ?? "") === "" && a.id !== current?.id)
      .map((a) => ({ value: a.id, label: a.name })),
  ];

  function save() {
    if (current === null) {
      create.mutate(
        {
          name,
          description,
          keywords: parseTags(keywords),
          parent_id: parentId === "" ? null : parentId,
        },
        { onSuccess: onClose },
      );
    } else {
      update.mutate(
        {
          id: current.id,
          body: {
            name,
            description,
            keywords: parseTags(keywords),
            move: (current.parent_id ?? "") !== parentId,
            parent_id: parentId === "" ? null : parentId,
          },
        },
        { onSuccess: onClose },
      );
    }
  }

  return (
    <Dialog.Root open onOpenChange={(open) => (open ? undefined : onClose())}>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup>
          <Dialog.Header>
            <Dialog.Title>
              {current === null
                ? t("knowledgeAdmin.areasPage.add")
                : t("knowledgeAdmin.areasPage.editTitle", { name: current.name })}
            </Dialog.Title>
          </Dialog.Header>
          <Dialog.Body className="flex flex-col gap-3">
            <Field name="area-name" label={t("knowledgeAdmin.areasPage.name")} required>
              <Input value={name} maxLength={255} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field name="area-parent" label={t("knowledgeAdmin.areasPage.parent")}>
              <OptionSelect
                options={parents}
                value={parentId}
                onValueChange={setParentId}
                placeholder=""
              />
            </Field>
            <Field
              name="area-keywords"
              label={t("knowledgeAdmin.areasPage.keywords")}
              hint={t("knowledgeAdmin.areasPage.keywordsHint")}
            >
              <Input value={keywords} onChange={(e) => setKeywords(e.target.value)} />
            </Field>
            <Field name="area-description" label={t("knowledgeAdmin.areasPage.description")}>
              <Input
                value={description}
                maxLength={2000}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
            {current ? (
              <Typography variant="caption" tone="muted">
                {t("knowledgeAdmin.areasPage.idStays", { id: current.id })}
              </Typography>
            ) : null}
            {error == null ? null : <ErrorState error={error} variant="inline" />}
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant="ghost" onClick={onClose}>
              {t("button.cancel")}
            </Button>
            <Button
              disabled={name.trim() === ""}
              loading={create.isPending || update.isPending}
              onClick={save}
            >
              {t("button.save")}
            </Button>
          </Dialog.Footer>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
